/**
 * Pure seat-resolution for a match session.
 *
 * `isHost` is the single axis that drives board orientation, the 3D
 * player-name badges, camera, seat number, ownership, and the restore
 * path. Historically it was inferred inline in GameBoard.initSession with
 * an optimistic `true` default, which let a guest be misidentified as host
 * during the handshake window. This module makes the decision explicit and
 * testable; the caller (MatchConnection) is the single source of truth and
 * applies the resolved seat through one funnel.
 *
 * Pure: no I/O, no React, no Three.js. Branch order mirrors initSession.
 *
 * @param sessionMode  'load' | 'new' | 'join' | 'spectate'
 * @param props        the relevant GameBoard props
 *   { sessionId, isArenaMatch, joinRoomCode, instanceIsHost, isResumeJoin }
 * @param roomInfo     the server's join/resume reply (null until it lands).
 *                     For the guest 'join' path this is where the
 *                     authoritative `isHost` comes from.
 * @returns { isHost, kind } where
 *   kind ∈ 'host-resume' | 'host-new' | 'spectate' | 'replay'
 *        | 'guest-join' | 'guest-resume' | 'solo'
 */
export function resolveSeat({ sessionMode, props = {}, roomInfo = null }) {
  const { sessionId, isArenaMatch, joinRoomCode, instanceIsHost, isResumeJoin } = props;

  // Replay playback: no transport at all. The ReplayDriver feeds the fold
  // funnel. The viewer picks which seat's side of the table to sit at;
  // isHost carries that orientation exactly as it does for live seats.
  if (props.replayDriver) {
    return { isHost: props.replayViewpoint !== 'p2', kind: 'replay' };
  }

  // Multiplayer-instance host resume: host loaded a saved match and
  // recreated the room so the guest can rejoin.
  if (sessionMode === 'load' && sessionId && isArenaMatch && joinRoomCode && instanceIsHost) {
    return { isHost: true, kind: 'host-resume' };
  }

  // Fresh matchmaking host. Server-assigned room code, host creates it.
  if (isArenaMatch && joinRoomCode && sessionMode === 'new') {
    return { isHost: true, kind: 'host-new' };
  }

  if (sessionMode === 'spectate') {
    return { isHost: false, kind: 'spectate' };
  }

  // Guest. The seat is authoritative from the server, never inferred.
  if (sessionMode === 'join') {
    const isHost = !!roomInfo?.isHost;
    const resumed = !!(roomInfo?.resumed || isResumeJoin);
    return { isHost, kind: resumed ? 'guest-resume' : 'guest-join' };
  }

  // Defensive fallback for a malformed mount (no room, no join code). The
  // app is fully online. Every real flow (including server-backed solo and
  // bot matches) mounts with an arena room; nothing produces this shape
  // since the offline SessionLobby was removed. MatchConnection treats it
  // as a no-op connection kind.
  return { isHost: true, kind: 'solo' };
}
